import * as React from 'react';

import type { StepConfig, StepState } from '@/hooks/useStepper';
import { cn } from '@/utils';

/* -----------------------------------------------------------------------------
 * Stepper Root
 * -------------------------------------------------------------------------- */

interface StepperProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Array of step configurations */
  steps: StepConfig[];
  /** Function to get the state of each step */
  getStepState: (stepIndex: number) => StepState;
  /** Click handler for step navigation (receives 0-indexed step) */
  onStepClick?: (step: number) => void;
}

const Stepper = React.forwardRef<HTMLDivElement, StepperProps>(
  ({ className, steps, getStepState, onStepClick, ...props }, ref) => {
    const isClickable = !!onStepClick;
    const lastIndex = steps.length - 1;

    return (
      <div ref={ref} className={cn('flex w-full items-start', className)} {...props}>
        {steps.map((step, index) => {
          const state = getStepState(index);
          const isFirst = index === 0;
          const isLast = index === lastIndex;

          // Each step is an equal-width column with the dot centered inside it and
          // the label directly beneath. The label is constrained to its own column
          // (`w-full` + wrapping), so it can never overflow into a neighbouring
          // step no matter how many steps there are or how narrow the stepper is.
          // Half-width connectors flank the dot: the left half is the tail of the
          // previous connector, the right half the head of the next one. They keep
          // the same "completed => primary" colouring as before and are hidden at
          // the two ends so the track doesn't spill past the first/last dot.
          return (
            <div
              key={step.id}
              className={cn(
                'flex min-w-0 flex-1 flex-col items-center',
                isClickable && 'cursor-pointer',
              )}
              onClick={isClickable ? () => onStepClick(index) : undefined}
              role={isClickable ? 'button' : undefined}
              tabIndex={isClickable ? 0 : undefined}
              onKeyDown={
                isClickable
                  ? e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onStepClick(index);
                      }
                    }
                  : undefined
              }
              title={step.title}
            >
              {/* Indicator row: connector halves keep the dot centred in the column */}
              <div className="flex w-full items-center">
                <StepConnector
                  state={isFirst ? 'pending' : getStepState(index - 1)}
                  className={cn('flex-1', isFirst && 'invisible')}
                />
                <StepIndicator state={state} className="shrink-0" />
                <StepConnector state={state} className={cn('flex-1', isLast && 'invisible')} />
              </div>

              {/* Label: centred under the dot, wraps within the column */}
              <span
                className={cn(
                  'mt-1.5 w-full text-center text-sm leading-tight break-words',
                  state === 'active' ? 'text-primary font-medium' : 'text-muted-foreground',
                )}
              >
                {step.title}
              </span>
            </div>
          );
        })}
      </div>
    );
  },
);
Stepper.displayName = 'Stepper';

/* -----------------------------------------------------------------------------
 * Step Indicator (dot or number)
 * -------------------------------------------------------------------------- */

interface StepIndicatorProps extends React.HTMLAttributes<HTMLDivElement> {
  /** The state of this step */
  state: StepState;
  /** Optional step index (0-indexed, displayed as 1-indexed). If not provided, renders a dot. */
  step?: number;
  /** Size variant */
  size?: 'sm' | 'default' | 'lg';
}

const StepIndicator = React.forwardRef<HTMLDivElement, StepIndicatorProps>(
  ({ className, step, state, size = 'default', ...props }, ref) => {
    const showNumber = step !== undefined;

    // Dot sizes (when no number)
    const dotSizeClasses = {
      sm: 'h-2 w-2',
      default: 'h-2.5 w-2.5',
      lg: 'h-3 w-3',
    };

    // Number sizes (when step is provided)
    const numberSizeClasses = {
      sm: 'h-6 w-6 text-xs',
      default: 'h-8 w-8 text-sm',
      lg: 'h-10 w-10 text-base',
    };

    const dotStateClasses = {
      pending: 'bg-muted-foreground/30',
      active: 'bg-primary',
      completed: 'bg-primary',
    };

    const numberStateClasses = {
      pending: 'border-2 border-muted-foreground/30 bg-background text-muted-foreground',
      active: 'border-2 border-primary bg-primary text-primary-foreground',
      completed: 'border-2 border-primary bg-primary text-primary-foreground',
    };

    if (showNumber) {
      return (
        <div
          ref={ref}
          className={cn(
            'flex items-center justify-center rounded-full font-medium transition-colors',
            numberSizeClasses[size],
            numberStateClasses[state],
            className,
          )}
          {...props}
        >
          {step + 1}
        </div>
      );
    }

    return (
      <div
        ref={ref}
        className={cn(
          'rounded-full transition-colors',
          dotSizeClasses[size],
          dotStateClasses[state],
          className,
        )}
        {...props}
      />
    );
  },
);
StepIndicator.displayName = 'StepIndicator';

/* -----------------------------------------------------------------------------
 * Step Connector (the line between steps)
 * -------------------------------------------------------------------------- */

interface StepConnectorProps extends React.HTMLAttributes<HTMLDivElement> {
  /** The state of the preceding step */
  state: StepState;
}

const StepConnector = React.forwardRef<HTMLDivElement, StepConnectorProps>(
  ({ className, state, ...props }, ref) => {
    const stateClasses = {
      pending: 'bg-muted-foreground/30',
      active: 'bg-muted-foreground/30',
      completed: 'bg-primary',
    };

    return (
      <div
        ref={ref}
        className={cn('h-0.5 transition-colors', stateClasses[state], className)}
        {...props}
      />
    );
  },
);
StepConnector.displayName = 'StepConnector';

export { StepConnector, StepIndicator, Stepper };
